import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import "../../css/nnat/nnat.css";
import Header from '../Header';
import Footer from '../Footer';
import { useNavigate } from "react-router-dom";
import FAQNNAT from "./FAQNNAT";
import NnatExamples from "./NnatExamples";

export default function HomePage() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false); // State for mobile detection
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768); // Adjust 768px to your breakpoint
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Call once to set initial state

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div className="homepage-container">
      <Header />
      <section className="cogat-description-section">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="cogat-content"
        >
          {/* Left side: Text */}
          <div className="nnat-text">
            <h2 className="nnat-header">What is the Naglieri Nonverbal Ability Test (NNAT)?</h2>
            <p className="nnat-paragraph">
              The Naglieri Nonverbal Ability Test (NNAT) is a standardized test that measures a student’s nonverbal reasoning and problem-solving skills. Unlike traditional tests, NNAT focuses on pattern recognition, spatial reasoning, and logical thinking without relying on language or math skills. Many schools use NNAT for gifted and talented program placements.
            </p>
          </div>


          {/* Right side: Image (visible only if not mobile) */}
          {!isMobile && (
            <div className="cogat-image-container">
              <img src="/nnat.PNG" alt="CoGAT" className="cogat-image" />
            </div>
          )}
        </motion.div>
      </section>


      {/* Section: CoGAT Key Areas */}
      <section className="nnat-section">
      <div className="nnat-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="nnat-header-container"
        >
          <h2 className="nnat-title">Key Areas Assessed in NNAT</h2>
          <p className="nnat-description">
            The NNAT (Naglieri Nonverbal Ability Test) evaluates students' nonverbal reasoning skills through pattern recognition and spatial visualization.
          </p>
        </motion.div>

        <div className="nnat-grid">
          {/* Pattern Completion */}
          <motion.div className="nnat-card" whileHover={{ scale: 1.05 }}>
            <h3 className="nnat-card-title">🧩 Pattern Completion</h3>
            <p className="nnat-card-text">
              Identifies the missing portion of a visual pattern.
            </p>
          </motion.div>

          {/* Reasoning by Analogy */}
          <motion.div className="nnat-card" whileHover={{ scale: 1.05 }}>
            <h3 className="nnat-card-title">🔄 Reasoning by Analogy</h3>
            <p className="nnat-card-text">
              Recognizes relationships between shapes and completes the analogy.
            </p>
          </motion.div>

          {/* Serial Reasoning */}
          <motion.div className="nnat-card" whileHover={{ scale: 1.05 }}>
            <h3 className="nnat-card-title">📊 Serial Reasoning</h3>
            <p className="nnat-card-text">
              Identifies patterns and selects the next shape in a sequence.
            </p>
          </motion.div>

          {/* Spatial Visualization */}
          <motion.div className="nnat-card" whileHover={{ scale: 1.05 }}>
            <h3 className="nnat-card-title">📐 Spatial Visualization</h3>
            <p className="nnat-card-text">
              Mentally manipulates shapes to determine how they fit together.
            </p>
          </motion.div>
        </div>
      </div>
    </section>


      {/* Section 3: IAAT Test Details */}
      <section className="iaat-test-details-section">
        <div className="iaat-content">
          {/* Test Overview - visible only if not mobile */}
          {!isMobile && (
            <>
              <motion.h2
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="iaat-header"
              >
                Test Overview
              </motion.h2>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="iaat-paragraph2"
              >
                The Cognitive Abilities Test (COGAT) measures a student's reasoning and problem-solving abilities in areas such as verbal, quantitative, and non-verbal reasoning, providing valuable insights for academic placement and development.
              </motion.p>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="iaat-test-info"
              >
                <p><strong>Test Duration:</strong> 60-75 minutes</p>
                <p><strong>Format:</strong> Typically paper-based</p>
                <p><strong>Scoring:</strong> Scored based on standard norms for comparison</p>
              </motion.div>
            </>
          )}
        </div>
      </section>

      <NnatExamples />
      <FAQNNAT />
      {/* Footer */}
      <Footer />
    </div>
  );
}
